import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { Section } from "components/MainSectionTemplate/types";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { useHasAnyTags } from "components/Tag/hooks";
import { contactMainSection } from "contact/sections/main";
import { otherDetailSection } from "contact/sections/otherDetailSection";
import { passportSection } from "contact/sections/passportSection";
import { socialsSection } from "contact/sections/socialsSection";
import { contactToFormState, ContactFormState, getContactFieldMetaData } from "shared/utils/fieldsHelper/contactFields";
import React from "react";
import { ContactWorksAtSection } from "./ContactWorksAtSection";
import { ContactAliasesSection } from "./ContactAliasesSection";
import { invalidateContactCache } from "utilities/api/contactQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useHasMultipleStatuses } from "components/Status/hooks";

interface Props {
  sidePanelData: server.dto.ContactSidePanelData
  refetch: () => Promise<void>
}

export const ContactSidePanelDetails = (props: Props) => {
  const sidePanelData = props.sidePanelData;
  const { contact } = sidePanelData;
  const [formState, setFormState] = React.useState<ContactFormState>(contactToFormState(sidePanelData.contact, serverReference));

  React.useEffect(() => {
    if (props.sidePanelData) {
      setFormState(contactToFormState(props.sidePanelData.contact, serverReference));
    }
  }, [props.sidePanelData]);

  const hasMultipleStatuses = useHasMultipleStatuses("contact", contact.clientId);
  const hasAnyTags = useHasAnyTags("contact");
  const fieldMetaData = getContactFieldMetaData(
    site.currentEnvironment,
    formState,
    contact.permissions,
    hasAnyTags,
    hasMultipleStatuses
  );
  const canInPageEdit = sidePanelData.contact.permissions.canModify;

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      const [firstName, insertion, lastName] = value as string[];
      setFormState(prevState => ({
        ...prevState,
        firstName,
        insertion,
        lastName
      }));
    } else if (key === "department") {
      setFormState(prevState => ({
        ...prevState,
        department: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    } else if (key === "tags") {
      setFormState(prevState => ({
        ...prevState,
        tags: value
      }));
    } else if (key === "owner") {
      setFormState(prevState => ({
        ...prevState,
        owner: value
      }));
    } else if (key === "emailAddresses") {
      setFormState(prevState => ({
        ...prevState,
        emailAddresses: value[0],
        isEmailBlacklisted: value[1]
      }));
    } else if (key === "mobileNumbers") {
      setFormState(prevState => ({
        ...prevState,
        mobileNumbers: value
      }));
    } else if (key === "gender") {
      setFormState(prevState => ({
        ...prevState,
        gender: value
      }));
    } else if (key === "position") {
      setFormState(prevState => ({
        ...prevState,
        position: value
      }));
    } else if (key === "language") {
      setFormState(prevState => ({
        ...prevState,
        culture: value
      }));
    } else if (key === "landline") {
      setFormState(prevState => ({
        ...prevState,
        landlineNumbers: value
      }));
    } else if (key === "salutation") {
      setFormState(prevState => ({
        ...prevState,
        salutation: value
      }));
    } else if (key === "attn") {
      setFormState(prevState => ({
        ...prevState,
        attn: value
      }));
    } else if (key === "homeAddress") {
      setFormState(prevState => ({
        ...prevState,
        homeAddress: {
          ...value
        }
      }));
    } else if (key === "twitter") {
      setFormState(prevState => ({
        ...prevState,
        twitter: value
      }));
    } else if (key === "website") {
      setFormState(prevState => ({
        ...prevState,
        website: value
      }));
    } else if (key === "facebook") {
      setFormState(prevState => ({
        ...prevState,
        facebook: value
      }));
    } else if (key === "networkPath") {
      setFormState(prevState => ({
        ...prevState,
        networkPath: value
      }));
    } else if (key === "socialSecurity") {
      setFormState(prevState => ({
        ...prevState,
        socialSecurity: value
      }));
    } else if (key === "birthdate") {
      setFormState(prevState => ({
        ...prevState,
        birthdate: value ? value.toDate() : null
      }));
    } else if (key === "birthplace") {
      setFormState(prevState => ({
        ...prevState,
        birthplace: value
      }));
    } else if (key === "maritalStatus") {
      setFormState(prevState => ({
        ...prevState,
        maritalStatus: value
      }));
    } else if (key === "additionalInformation") {
      setFormState(prevState => ({
        ...prevState,
        additionalInformation: value
      }));
    } else if (key === "nationality") {
      setFormState(prevState => ({
        ...prevState,
        nationality: value
      }));
    } else if (key === "passportName") {
      setFormState(prevState => ({
        ...prevState,
        passportName: value
      }));
    } else if (key === "passportNumber") {
      setFormState(prevState => ({
        ...prevState,
        passportNumber: value
      }));
    } else if (key === "passportIssued") {
      setFormState(prevState => ({
        ...prevState,
        passportIssued: value ? value.toDate() : null
      }));
    } else if (key === "passportExpiration") {
      setFormState(prevState => ({
        ...prevState,
        passportExpiration: value ? value.toDate() : null
      }));
    }
    else {
      console.warn(`Unhandled key in ContactSidePanelDetails: ${key}`);
      return;
    }
  }

  const queryClient = useQueryClient();

  const onSuccessfulUpdate = async () => {
    await props.refetch();
    invalidateContactCache(queryClient, contact.contactId);
  }

  const mainSection: Section = { ...contactMainSection(formState, fieldMetaData, undefined, undefined, undefined, true), hideTitle: true };
  const deletedSection: Section = {
    key: "deleted",
    type: "custom",
    hidden: fieldMetaData.deleted.fields().hidden,
    render: () => <DeletedRow />,
  };
  const notValidatedSection: Section = {
    key: "isValidated",
    type: "custom",
    hidden: fieldMetaData.isValidated.fields().hidden,
    render: () => <NotValidatedRow
      canApprove={!fieldMetaData.isValidated.fields().editDisabled}
      sublabel={RESX.Planning.InputFromWebform}
      approveEndpoint={() => window.CoreApi.Contact.Validate({
        contactId: formState.contactId
      })}
    />,
  };

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onSuccessfulUpdate={onSuccessfulUpdate}
    onValueChanged={onValueChanged}
    sections={[
      deletedSection,
      notValidatedSection,
      mainSection,
      socialsSection(formState, fieldMetaData),
      otherDetailSection(formState, fieldMetaData),
      passportSection(formState, fieldMetaData),
    ]}
    additionalContent={<>
      <ContactWorksAtSection worksAt={sidePanelData.worksAt} />
      <ContactAliasesSection
        aliases={sidePanelData.aliases}
        canModify={canInPageEdit}
        onDeleted={props.refetch}
      />
    </>}
  />
}
